import React, { Dispatch, SetStateAction } from "react";
import { SxStyleProp } from "theme-ui";
import { FilterIcon, CloseIcon } from "../icons";
import {
  Popover as PopoverBase,
  PopoverTrigger,
  PopoverContent,
} from "../Popover/Popover";
import Box, { Props as BoxProps } from "../Box/Box";
import Flex from "../Flex/Flex";
import Text from "../Text/Text";
import Tag, { TagClose } from "../Tag/Tag";
import Button, { Props as ButtonProps } from "../Button/Button";
import InputBase from "../Input/Input";

interface SearchTableContext {
  isOpen?: boolean;
  onOpen: (event: any) => void;
  onClose: (event: any) => void;
}

const SearchTableContext = React.createContext<SearchTableContext>(
  {} as SearchTableContext,
);
const useSearchTableContext = () => React.useContext(SearchTableContext);

const Popover: React.FunctionComponent = ({ children, ...restProps }) => {
  const { isOpen, onClose, onOpen } = useSearchTableContext();
  return (
    <PopoverBase
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      {...restProps}
    >
      {children}
    </PopoverBase>
  );
};

interface InputProps extends React.HTMLAttributes<HTMLInputElement> {
  sxWrapper?: SxStyleProp;
  filters?: { filter: { [key: string]: string }; label: string }[];
  placeholder?: string;
  defaultValue?: string;
  value?: string | number;
  buttonProps?: ButtonProps & BoxProps;
  onChange?: (event: React.FormEvent<HTMLInputElement>, value?: any) => void;
  setFilters: Dispatch<SetStateAction<any[]>>;
  onClear?: () => void;
  onClose: () => void;
  icon?: React.ReactElement;
}

const Input: React.FunctionComponent<InputProps> = ({
  sxWrapper,
  filters,
  placeholder,
  defaultValue = "",
  onChange,
  setFilters,
  onClear,
  onClose,
  buttonProps,
  icon = <FilterIcon color="primary" size="20px" />,
  ...restProps
}) => {
  const { isOpen, onOpen } = useSearchTableContext();
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const inputElement = React.useRef<HTMLDivElement>(null);
  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    setInternalValue(event.currentTarget.value);
    if (onChange) {
      onChange(event, event.currentTarget.value);
    }
  };

  return (
    <PopoverTrigger>
      <Flex
        align="center"
        sx={{ flex: 1, position: "relative", ...(sxWrapper as any) }}
      >
        <InputBase
          as="div"
          sx={{
            display: "flex",
            position: "relative",
            flexFlow: "row wrap",
            minHeight: "38px",
            pr: 6,
            py: 0,
            pb: 1,
          }}
          autoComplete="new-password"
          onClick={() => {
            const node = inputElement.current;
            if (node) {
              node.focus();
            }
          }}
        >
          {filters &&
            filters.map(filter => {
              return (
                <Tag
                  key={filter.label}
                  sx={{
                    mr: 2,
                    mt: 1,
                    minHeight: "30px",
                  }}
                >
                  <Text
                    sx={{
                      maxWidth: "calc(100% - 16px)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: ".75rem",
                    }}
                  >
                    {filter.label}
                  </Text>
                  <TagClose
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setFilters(currentFilters =>
                        currentFilters.filter(f => f.label !== filter.label),
                      );
                      onClose();
                    }}
                    size="16px"
                  />
                </Tag>
              );
            })}
          <InputBase
            ref={inputElement}
            placeholder={placeholder}
            value={internalValue}
            onChange={handleChange}
            onFocus={onOpen}
            sx={{
              width: filters && filters.length ? "auto" : "100%",
              border: "none",
              ":focus": {
                outline: "none",
              },
              px: 0,
            }}
            {...restProps}
            autoComplete="new-password"
          />
          {!!internalValue && (
            <Text
              as="button"
              fontSize={0}
              sx={{
                position: "absolute",
                top: 0,
                right: 8,
                height: "100%",
                display: "flex",
                alignItems: "center",
                color: "primary",
                cursor: "pointer",
                appearance: "none",
                border: 0,
                bg: "transparent",
                ":focus": {
                  outline: "none",
                },
              }}
              onClick={() => {
                setInternalValue("");
                if (onClear) {
                  onClear();
                }
              }}
            >
              Clear
            </Text>
          )}
        </InputBase>
        <Button
          sx={{
            position: "absolute",
            right: 0,
            height: "100%",
            px: 2,
            py: 1,
            color: "border",
            bg: "white",
            border: "1px solid",
            borderRadius: "0 0.25rem 0.25rem 0",
            ":focus": {
              outline: "none",
            },
          }}
          {...buttonProps}
        >
          {isOpen ? <CloseIcon color="primary" size="20px" /> : icon}
        </Button>
      </Flex>
    </PopoverTrigger>
  );
};

const InputResults: React.FunctionComponent<{ sx?: SxStyleProp }> = ({
  sx,
  ...restProps
}) => {
  return (
    <PopoverContent>
      <Box
        sx={{
          my: 2,
          width: "100%",
          border: "1px solid",
          borderColor: "border",
          borderRadius: "default",
          bg: "white",
          ...(sx as any),
        }}
        {...restProps}
      />
    </PopoverContent>
  );
};

interface Props extends SearchTableContext {
  sx?: SxStyleProp;
}

interface SearchTableComposition {
  Popover: React.FunctionComponent;
  Input: React.FunctionComponent<InputProps>;
  InputResults: React.FunctionComponent;
}

const SearchTable: React.FunctionComponent<Props> &
  SearchTableComposition = props => {
  const { children, isOpen, onOpen, onClose } = props;
  return (
    <SearchTableContext.Provider value={{ isOpen, onOpen, onClose }}>
      {children}
    </SearchTableContext.Provider>
  );
};

SearchTable.Popover = Popover;
SearchTable.Input = Input;
SearchTable.InputResults = InputResults;

export { SearchTable };
